import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useSettlementSession } from '../../../hooks/useSettlementSession';
import { useSettlement } from '../../../contexts/SettlementContext';
import {
  REVIEW_ORGANIZE_HUB_FRAME_ID,
  countHubSectionsDone,
  mergeHubSections,
  saveHubSectionsAndSyncDt1,
  type HubSectionKey,
} from './reviewOrganizeHub';

export interface ChecklistItemDef {
  id: string;
  title: string;
  description: string;
}

interface DebtReviewSectionShellProps {
  estateId: string | undefined;
  title: string;
  sectionIndex: number;
  checklistFrameId: string;
  items: readonly ChecklistItemDef[];
  hubSectionKey: HubSectionKey;
  children?: ReactNode;
}

export default function DebtReviewSectionShell({
  estateId,
  title,
  sectionIndex,
  checklistFrameId,
  items,
  hubSectionKey,
  children,
}: DebtReviewSectionShellProps) {
  const navigate = useNavigate();
  const { isArchived } = useSettlement();
  const itemsKey = items.map((i) => i.id).join('|');
  const [checks, setChecks] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(items.map((i) => [i.id, false]))
  );
  const [hubDoneCount, setHubDoneCount] = useState(0);
  const [sectionBusy, setSectionBusy] = useState(false);
  const { loadFrameData, saveFrameData, updateStepProgress, isLoading, error } = useSettlementSession(
    estateId ?? null,
    { readOnly: isArchived }
  );

  const reloadHubCount = useCallback(async () => {
    if (!estateId) return;
    const hub = await loadFrameData(REVIEW_ORGANIZE_HUB_FRAME_ID);
    const sections = mergeHubSections(hub?.data as Record<string, unknown> | undefined);
    setHubDoneCount(countHubSectionsDone(sections));
  }, [estateId, loadFrameData]);

  useEffect(() => {
    if (!estateId) return;
    const load = async () => {
      const framed = await loadFrameData(checklistFrameId);
      const rawChecks = framed?.data?.checks as Record<string, boolean> | undefined;
      setChecks(Object.fromEntries(items.map(({ id }) => [id, !!rawChecks?.[id]])));
      await reloadHubCount();
    };
    void load();
  }, [estateId, checklistFrameId, itemsKey, loadFrameData, reloadHubCount, items]);

  const persistChecks = async (next: Record<string, boolean>) => {
    if (!estateId || isArchived) return;
    const framed = await loadFrameData(checklistFrameId);
    const prev =
      typeof framed?.data === 'object' && framed?.data ? (framed.data as Record<string, unknown>) : {};
    await saveFrameData(checklistFrameId, { ...prev, checks: next });
    setChecks(next);
  };

  const toggle = async (itemId: string) => {
    const next = { ...checks, [itemId]: !checks[itemId] };
    await persistChecks(next);
  };

  const backPath = estateId ? `/sframe-199/${estateId}` : '/sframe-199';

  const markSectionComplete = async () => {
    if (!estateId || isArchived) return;
    setSectionBusy(true);
    try {
      const hub = await loadFrameData(REVIEW_ORGANIZE_HUB_FRAME_ID);
      const sections = mergeHubSections(hub?.data as Record<string, unknown> | undefined);
      sections[hubSectionKey] = true;
      await saveHubSectionsAndSyncDt1(loadFrameData, saveFrameData, updateStepProgress, sections);
      await reloadHubCount();
    } finally {
      setSectionBusy(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      ) : null}
      <button
        type="button"
        onClick={() => navigate(backPath)}
        className="flex items-center gap-2 mb-6 text-sm text-stone-500 hover:text-stone-700"
      >
        <ChevronLeft className="w-4 h-4 shrink-0" aria-hidden />
        Back to Review & Organize
      </button>

      <h1 className="text-2xl font-semibold text-stone-800 mb-6">{title}</h1>

      <div className="mb-6">
        <div className="flex justify-between text-sm text-stone-500 mb-1">
          <span>
            Section {sectionIndex} of 5 — {hubDoneCount} of 5 sections completed
          </span>
          <span>{Math.round((hubDoneCount / 5) * 100)}%</span>
        </div>
        <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
          <div
            className="h-2 bg-cyan-600 rounded-full transition-all"
            style={{ width: `${(hubDoneCount / 5) * 100}%` }}
          />
        </div>
      </div>

      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.id} className="bg-white rounded-xl border border-stone-200 p-5 flex items-start gap-4">
            <input
              type="checkbox"
              checked={!!checks[item.id]}
              disabled={isLoading || isArchived}
              onChange={() => void toggle(item.id)}
              className="mt-1 h-5 w-5 rounded border-stone-300 text-cyan-600 cursor-pointer disabled:opacity-50"
            />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-stone-800">{item.title}</p>
              <p className="text-stone-500 text-sm mt-1">{item.description}</p>
            </div>
          </div>
        ))}
      </div>

      {children}

      <div className="mt-8">
        <button
          type="button"
          disabled={isArchived || sectionBusy || isLoading}
          onClick={() => void markSectionComplete()}
          className="w-full rounded-xl bg-cyan-600 text-white font-medium py-3 px-4 hover:bg-cyan-700 disabled:opacity-50 transition-colors"
        >
          {sectionBusy ? 'Saving…' : 'Mark section complete'}
        </button>
      </div>
    </div>
  );
}
